import HeroSection from "@/components/LandingPage/Hero/HeroSection.vue";
import NavbarSection from "@/components/LandingPage/Navbar/NavbarSection.vue";

const PARTICLE_COUNT = 72;
const LINK_DISTANCE = 128;
const MOUSE_RADIUS = 156;

export default {
  name: "LandingPage",
  components: {
    HeroSection,
    NavbarSection,
  },
  data() {
    return ({
      particleState: null,
    });
  },
  mounted() {
    this.setupParticles();
  },
  beforeUnmount() {
    this.teardownParticles();
  },
  methods: {
    setupParticles() {
      const canvas = this.$refs.particleCanvas;

      if (!canvas) {
        return;
      }

      const context = canvas.getContext("2d");

      if (!context) {
        return;
      }

      const pointer = { x: window.innerWidth / 2, y: window.innerHeight / 2, active: false };
      const particles = [];

      const randomParticle = (width, height) => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.45,
        vy: (Math.random() - 0.5) * 0.45,
        radius: Math.random() * 1.8 + 1.2,
      });

      const resize = () => {
        const { innerWidth, innerHeight, devicePixelRatio } = window;
        const ratio = devicePixelRatio || 1;

        canvas.width = innerWidth * ratio;
        canvas.height = innerHeight * ratio;
        canvas.style.width = `${innerWidth}px`;
        canvas.style.height = `${innerHeight}px`;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);

        if (!particles.length) {
          for (let index = 0; index < PARTICLE_COUNT; index += 1) {
            particles.push(randomParticle(innerWidth, innerHeight));
          }
          return;
        }

        particles.forEach((particle) => {
          particle.x = Math.min(particle.x, innerWidth);
          particle.y = Math.min(particle.y, innerHeight);
        });
      };

      const drawConnections = () => {
        for (let i = 0; i < particles.length; i += 1) {
          const particle = particles[parseInt(i, 10)];

          for (let j = i + 1; j < particles.length; j += 1) {
            const other = particles[parseInt(j, 10)];
            const dx = particle.x - other.x;
            const dy = particle.y - other.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance > LINK_DISTANCE) {
              continue;
            }

            const opacity = 1 - distance / LINK_DISTANCE;
            context.strokeStyle = `rgba(255, 255, 255, ${opacity * 0.18})`;
            context.lineWidth = 1;
            context.beginPath();
            context.moveTo(particle.x, particle.y);
            context.lineTo(other.x, other.y);
            context.stroke();
          }

          if (pointer.active) {
            const dx = particle.x - pointer.x;
            const dy = particle.y - pointer.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < MOUSE_RADIUS) {
              context.strokeStyle = `rgba(255, 21, 26, ${(1 - distance / MOUSE_RADIUS) * 0.38})`;
              context.beginPath();
              context.moveTo(particle.x, particle.y);
              context.lineTo(pointer.x, pointer.y);
              context.stroke();
            }
          }
        }
      };

      const step = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;

        context.clearRect(0, 0, width, height);

        particles.forEach((particle) => {
          if (pointer.active) {
            const dx = pointer.x - particle.x;
            const dy = pointer.y - particle.y;
            const distance = Math.sqrt(dx * dx + dy * dy) || 1;

            if (distance < MOUSE_RADIUS) {
              const force = (1 - distance / MOUSE_RADIUS) * 0.045;
              particle.vx -= (dx / distance) * force;
              particle.vy -= (dy / distance) * force;
            }
          }

          particle.x += particle.vx;
          particle.y += particle.vy;
          particle.vx *= 0.992;
          particle.vy *= 0.992;

          if (particle.x <= 0 || particle.x >= width) {
            particle.vx *= -1;
            particle.x = Math.max(0, Math.min(width, particle.x));
          }

          if (particle.y <= 0 || particle.y >= height) {
            particle.vy *= -1;
            particle.y = Math.max(0, Math.min(height, particle.y));
          }

          context.fillStyle = "rgba(245, 242, 238, 0.82)";
          context.beginPath();
          context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
          context.fill();
        });

        drawConnections();
        this.particleState.frame = window.requestAnimationFrame(step);
      };

      const handlePointerMove = (event) => {
        pointer.x = event.clientX;
        pointer.y = event.clientY;
        pointer.active = true;
      };

      const handlePointerLeave = () => {
        pointer.active = false;
      };

      resize();

      window.addEventListener("resize", resize);
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerleave", handlePointerLeave);

      this.particleState = {
        canvas,
        context,
        particles,
        pointer,
        resize,
        handlePointerMove,
        handlePointerLeave,
        frame: window.requestAnimationFrame(step),
      };
    },
    teardownParticles() {
      if (!this.particleState) {
        return;
      }

      window.cancelAnimationFrame(this.particleState.frame);
      window.removeEventListener("resize", this.particleState.resize);
      window.removeEventListener("pointermove", this.particleState.handlePointerMove);
      window.removeEventListener("pointerleave", this.particleState.handlePointerLeave);
      this.particleState = null;
    },
  },
};
